import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { Button } from '@crowdex/ui/components/button';
import { cn } from '@crowdex/ui/lib/utils';
import type {
  CheckpointConflict,
  CheckpointRevertResolution,
  CheckpointRevertResult,
} from '@crowdex/workspace-protocol';
import { GitBranch, LoaderCircle, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { messageChangedFiles, messageCheckpoint } from '@/lib/conversations/message-changes';
import { diffExcerpt } from '@/lib/conversations/message-part/step-rows';
import { basename } from '@/lib/desktop-paths';
import { errorMessage } from '@/lib/error-message';
import { CheckpointConflicts, conflictResolution } from './checkpoint-conflicts';
import { TRANSCRIPT_CARD } from './command-card';
import { FileDiffHover } from './file-diff-hover';

const LISTED_FILES = 4;

function Counts({ additions, deletions }: { additions: number; deletions: number }) {
  return (
    <span className="shrink-0 font-mono text-xs tabular-nums">
      {additions > 0 && <span className="text-success">+{additions}</span>}
      {additions > 0 && deletions > 0 && ' '}
      {deletions > 0 && <span className="text-destructive">−{deletions}</span>}
    </span>
  );
}

/**
 * What a turn changed, as a card (design D10–D11, D45): one file shows its 2–3 key lines, more
 * files their list with their counts. Its one action, Review, opens the turn's diff in the
 * Review tab; the excerpt itself opens it too. Undo asks first when files someone else may have
 * changed would be put back: they are listed, and stay as they are unless the user says so.
 */
export function MessageChangesCard({
  message,
  loadDiff,
  onReview,
  onUndo,
}: {
  message: CrowdexUIMessage;
  /** The turn's diff (one file's with `path`), from the workspace server that checkpointed it. */
  loadDiff?: (message: CrowdexUIMessage, path?: string) => Promise<string>;
  onReview?: (diff: string) => void;
  onUndo?: (
    message: CrowdexUIMessage,
    resolution?: CheckpointRevertResolution,
  ) => Promise<CheckpointRevertResult>;
}) {
  const [undoState, setUndoState] = useState<'idle' | 'pending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  // The conflicts an undo stopped on, waiting for the user's word.
  const [conflicts, setConflicts] = useState<readonly CheckpointConflict[] | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const files = messageChangedFiles(message);
  if (files.length === 0) return null;
  const review =
    loadDiff === undefined || onReview === undefined
      ? undefined
      : (path?: string) => {
          setError(null);
          void loadDiff(message, path).then(onReview, (cause: unknown) =>
            setError(errorMessage(cause)),
          );
        };
  const additions = files.reduce((total, file) => total + file.additions, 0);
  const deletions = files.reduce((total, file) => total + file.deletions, 0);
  const more = messageCheckpoint(message)?.truncated === true;
  const single = files.length === 1 && !more ? files[0] : undefined;
  const edit =
    single === undefined
      ? undefined
      : message.parts.findLast(
          (part): part is Extract<typeof part, { type: 'data-diff' }> =>
            part.type === 'data-diff' &&
            (part.data.path === single.path || part.data.path.endsWith(`/${single.path}`)),
        );
  const excerpt = edit === undefined ? [] : diffExcerpt(edit.data.oldText ?? '', edit.data.newText);
  async function runUndo(resolution?: CheckpointRevertResolution) {
    if (onUndo === undefined) return;
    setError(null);
    setUndoState('pending');
    try {
      const result = await onUndo(message, resolution);
      if (result.applied) {
        setConflicts(null);
        setUndoState('done');
        return;
      }
      setUndoState('idle');
      if (resolution !== undefined) setError('Some files changed meanwhile: check them again.');
      setConflicts(result.conflicts);
    } catch (cause) {
      setUndoState('idle');
      setError(errorMessage(cause));
    }
  }
  const confirm = conflicts !== null && undoState !== 'done' && (
    <div className="grid gap-2 px-3.5 pb-3" data-undo-confirm>
      <CheckpointConflicts
        conflicts={conflicts}
        onOverwriteChange={setOverwrite}
        overwrite={overwrite}
      />
      <div className="flex items-center gap-2">
        <Button
          className="h-8 rounded-control px-4"
          disabled={undoState === 'pending'}
          onClick={() => void runUndo(conflictResolution(conflicts, overwrite))}
          size="sm"
        >
          {overwrite ? 'Undo everything' : 'Undo the rest'}
        </Button>
        <Button
          className="h-8 rounded-control px-3"
          disabled={undoState === 'pending'}
          onClick={() => {
            setConflicts(null);
            setOverwrite(false);
            setError(null);
          }}
          size="sm"
          variant="ghost"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
  const undo =
    onUndo === undefined || undoState === 'done' ? null : (
      <Button
        aria-label="Undo these changes"
        className="h-8 rounded-control px-3"
        disabled={undoState === 'pending'}
        onClick={() => void runUndo()}
        size="sm"
        variant="ghost"
      >
        {undoState === 'pending' ? (
          <LoaderCircle className="size-3.5 animate-spin" />
        ) : (
          <Undo2 className="size-3.5" />
        )}
        Undo
      </Button>
    );
  const errorLine = error !== null && (
    <p className="px-3.5 pb-2.5 text-destructive text-xs" role="alert">
      {error}
    </p>
  );

  if (single !== undefined && excerpt.length > 0) {
    return (
      <section
        className={cn(TRANSCRIPT_CARD, 'group/changes relative overflow-hidden')}
        data-message-changes="excerpt"
      >
        {onUndo !== undefined && undoState !== 'done' && (
          <Button
            aria-label="Undo these changes"
            className="absolute top-1.5 right-14 size-7 rounded-control transition-opacity can-hover:opacity-0 group-hover/changes:opacity-100 focus-visible:opacity-100"
            disabled={undoState === 'pending'}
            onClick={() => void runUndo()}
            size="icon-sm"
            title="Undo"
            variant="ghost"
          >
            {undoState === 'pending' ? (
              <LoaderCircle className="size-3.5 animate-spin" />
            ) : (
              <Undo2 className="size-3.5" />
            )}
          </Button>
        )}
        <button
          className="block w-full px-3.5 py-2.5 text-left font-mono text-xs leading-5 transition-colors hover:bg-control/30 disabled:hover:bg-transparent"
          disabled={review === undefined}
          onClick={() => review?.(single.path)}
          title={review === undefined ? undefined : 'Open in Review'}
          type="button"
        >
          <span className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-foreground text-sm">
              {basename(single.path)}
            </span>
            <Counts additions={single.additions} deletions={single.deletions} />
          </span>
          {excerpt.map((line, index) => (
            <span
              className={cn(
                'block truncate',
                line.sign === '+' ? 'text-success' : 'text-destructive',
              )}
              // biome-ignore lint/suspicious/noArrayIndexKey: The excerpt's lines may repeat; their order is their identity.
              key={index}
            >
              {line.sign} {line.text.trim()}
            </span>
          ))}
        </button>
        {undoState === 'done' && (
          <p className="px-3.5 pb-2.5 text-muted-foreground text-xs">Undone</p>
        )}
        {errorLine}
        {confirm}
      </section>
    );
  }

  const shown = files.slice(0, LISTED_FILES);
  const shared = files.filter((file) => file.shared).length;
  const hidden = files.length - shown.length;
  return (
    <section className={cn(TRANSCRIPT_CARD, 'px-3.5 py-3')} data-message-changes="list">
      <header className="flex items-center gap-2.5">
        <GitBranch aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <p className="min-w-0 flex-1 truncate font-semibold text-foreground text-sm">
          {files.length}
          {more ? '+' : ''} {files.length === 1 && !more ? 'file changed' : 'files changed'}
        </p>
        <Counts additions={additions} deletions={deletions} />
      </header>
      <div className="mt-2 grid grid-cols-1 gap-x-6 sm:grid-cols-2">
        {shown.map((file) => (
          <FileDiffHover
            file={file}
            key={file.path}
            {...(loadDiff === undefined ? {} : { loadDiff: () => loadDiff(message, file.path) })}
            {...(review === undefined ? {} : { onOpen: () => review(file.path) })}
          />
        ))}
      </div>
      {hidden > 0 && <p className="type-meta mt-1 text-muted-foreground">+{hidden} more</p>}
      {shared > 0 && (
        <p className="type-meta mt-1 text-muted-foreground">
          Another conversation worked in this folder at the same time: {shared}{' '}
          {shared === 1 ? 'file' : 'files'} may hold its changes too.
        </p>
      )}
      {error !== null && (
        <p className="mt-2 text-destructive text-xs" role="alert">
          {error}
        </p>
      )}
      <footer className="mt-3 flex items-center gap-2">
        {review !== undefined && undoState !== 'done' && (
          <Button
            className="h-8 rounded-control px-4"
            onClick={() => review()}
            size="sm"
            variant="outline"
          >
            Review
          </Button>
        )}
        {conflicts === null && undo}
        {undoState === 'done' && <span className="text-muted-foreground text-sm">Undone</span>}
      </footer>
      {confirm !== false && <div className="-mx-3.5 mt-3">{confirm}</div>}
    </section>
  );
}
